import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { MANIFESTO, SERVICES } from "../data/content";
import { FEATURED_WORK, WORK_PROJECTS } from "../data/work";
import { DotStrip } from "../components/Brand";
import TigerDraw from "../components/TigerDraw";
import { FadeUp, MaskedLine, SectionTag } from "../components/Motion";
import { LedgerMarquee, RedRibbon } from "../components/Marquee";

const Hero = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [4, -4]), { stiffness: 60, damping: 20 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-5, 5]), { stiffness: 60, damping: 20 });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      ref={ref}
      onMouseMove={onMove}
      className="relative flex min-h-screen flex-col justify-center overflow-hidden"
      data-testid="hero-section"
      style={{ perspective: 1200 }}
    >
      <div className="noise-overlay" />
      <div className="pointer-events-none absolute inset-4 border border-white/10 md:inset-6" />
      <DotStrip className="absolute left-0 right-0 top-4 text-nexa-red/60 md:top-6" />
      <DotStrip className="absolute bottom-4 left-0 right-0 text-nexa-red/60 md:bottom-6" />

      <motion.div
        className="pointer-events-none absolute right-8 top-24 hidden w-56 xl:block 2xl:right-16 2xl:w-72"
        initial={{ opacity: 0, x: 80, rotate: 10 }}
        animate={{ opacity: 1, x: 0, rotate: 2 }}
        transition={{ delay: 1.5, duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
        data-testid="hero-tiger"
      >
        <div className="border border-white/15 bg-nexa-red p-3 shadow-[10px_10px_0_0_rgba(0,0,0,0.45)]">
          <TigerDraw className="h-auto w-full" />
        </div>
      </motion.div>

      <motion.div style={{ rotateX: rx, rotateY: ry }} className="mx-auto w-full max-w-[1600px] px-8 pt-24 md:px-16">
        <div className="flex flex-wrap items-end gap-x-16 gap-y-10">
          <h1 className="w-[68vw] max-w-[420px] md:max-w-[480px]" aria-label="Nexa Studio" data-testid="hero-wordmark">
            {["wm-stack-1", "wm-stack-2", "wm-stack-3"].map((f, i) => (
              <span key={f} className={`block overflow-hidden ${i > 0 ? "mt-2" : ""}`}>
                <motion.img
                  src={`/brand/${f}.svg`}
                  alt=""
                  style={{ aspectRatio: ["284/192", "284/190", "284/53"][i] }}
                  className="block w-full will-change-transform"
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1, delay: 0.15 + i * 0.15, ease: [0.16, 1, 0.3, 1] }}
                />
              </span>
            ))}
          </h1>

          <div className="mb-2 max-w-md">
            <MaskedLine delay={0.7}>
              <span className="font-display text-3xl uppercase leading-tight text-nexa-bone md:text-5xl">
                We don't decorate brands.
              </span>
            </MaskedLine>
            <MaskedLine delay={0.85}>
              <span className="font-display text-3xl uppercase leading-tight text-nexa-red md:text-5xl">We arm them.</span>
            </MaskedLine>
            <motion.p
              className="mt-6 text-sm leading-relaxed text-nexa-bone/60 md:text-base"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2, duration: 0.8 }}
            >
              An aggressive brand building, content and performance studio in Kolkata — carrying 12 years of
              Myndroot's scars, case files and trophies.
            </motion.p>
            <motion.div
              className="mt-8 flex flex-wrap gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.35, duration: 0.7 }}
            >
              <Link
                to="/work"
                data-testid="hero-cta-work"
                className="group flex items-center gap-3 bg-nexa-red px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] text-white transition-transform duration-200 hover:-translate-y-1 hover:bg-nexa-bone hover:text-nexa-black"
              >
                See the 12 year ledger
                <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-cta-contact"
                className="flex items-center gap-3 border border-white/25 px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] text-nexa-bone transition-colors duration-200 hover:border-nexa-red hover:text-nexa-red"
              >
                Start a project
              </Link>
            </motion.div>
          </div>
        </div>
      </motion.div>

      <motion.div
        className="absolute bottom-10 left-8 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-nexa-bone/40 md:left-16"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8 }}
      >
        <ArrowDown size={14} className="animate-bounce text-nexa-red" />
        Brand · Design · Communication
      </motion.div>
    </section>
  );
};

const Proposition = () => (
  <section className="relative border-b border-white/10" data-testid="proposition-section">
    <div className="mx-auto grid max-w-[1600px] gap-10 px-5 py-24 md:grid-cols-12 md:px-10 md:py-36">
      <div className="md:col-span-3">
        <SectionTag>The position</SectionTag>
      </div>
      <div className="md:col-span-9">
        <FadeUp>
          <h2 className="font-display text-4xl uppercase leading-[1.02] tracking-tight text-nexa-bone sm:text-5xl lg:text-7xl">
            Nexa Studio is not an agency. It is an <span className="text-nexa-red">attack unit</span> for brands that refuse to be ignored.
          </h2>
        </FadeUp>
        <FadeUp delay={0.15}>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-nexa-bone/60 md:text-lg">
            Twelve years of Myndroot's battle testing — from FIFA U-17 to the IPL, from craft beer to corporate
            film — now aimed at one thing: brands that take market share, not just shelf space. Brand building,
            social media, performance marketing, content, podcasts, shoots and design. One roof. One drumbeat.
          </p>
        </FadeUp>
        <FadeUp delay={0.2}>
          <div className="mt-10 max-w-2xl border-l-2 border-nexa-red pl-6" data-testid="guardian-vow">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-nexa-red">The guardian vow</p>
            <p className="mt-3 text-base leading-relaxed text-nexa-bone/70 md:text-lg">
              Kshetrapala Bhairava, the fierce sentinel of the sacred field, never sleeps at his post. Every brand
              owns a kshetra: a market, a feed, a place in people's minds. Nexa Studio stands guard over yours.
            </p>
          </div>
        </FadeUp>
        <FadeUp delay={0.25}>
          <Link to="/philosophy" className="link-line mt-8 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-nexa-red" data-testid="proposition-link-philosophy">
            Read the manifesto <ArrowRight size={14} />
          </Link>
        </FadeUp>
      </div>
    </div>
  </section>
);

const SelectedWork = () => {
  const featured = FEATURED_WORK.map((f) => ({
    ...f,
    id: WORK_PROJECTS.find((p) => p.name === f.name)?.id,
  }));
  return (
    <section className="relative" data-testid="selected-work-section">
      <div className="mx-auto max-w-[1600px] px-5 py-24 md:px-10 md:py-36">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <SectionTag>Selected work</SectionTag>
            <h2 className="mt-4 font-display text-5xl uppercase leading-none text-nexa-bone md:text-7xl">
              The 12 year<br />
              <span className="text-nexa-red">ledger.</span>
            </h2>
          </div>
          <p className="max-w-sm text-sm leading-relaxed text-nexa-bone/50">
            Every engagement below was executed at Myndroot — the agency Nexa Studio was born from. Same hands.
            Sharper teeth.
          </p>
        </div>

        <div className="mt-14 grid gap-5 md:grid-cols-12">
          {featured.map((c, i) => (
            <FadeUp
              key={c.name}
              delay={(i % 3) * 0.1}
              className={i % 3 === 0 ? "md:col-span-7" : "md:col-span-5"}
            >
              <Link
                to={`/work/${c.id}`}
                className="group relative flex h-72 flex-col justify-between overflow-hidden border border-white/10 bg-nexa-surface p-7 transition-colors duration-300 hover:border-nexa-red md:h-80"
                data-testid={`work-card-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                {c.img && (
                  <div className="absolute inset-0">
                    <img src={c.img} alt={c.name} className="h-full w-full object-cover opacity-40 grayscale transition-all duration-500 group-hover:opacity-55 group-hover:grayscale-0" />
                    <div className="absolute inset-0 bg-gradient-to-t from-nexa-black via-nexa-black/40 to-transparent" />
                  </div>
                )}
                <div className="relative flex items-start justify-between">
                  <span className="font-mono text-xs tracking-[0.25em] text-nexa-red">{String(i + 1).padStart(2, "0")}</span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-nexa-bone/50">{c.sector}</span>
                </div>
                <div className="relative">
                  <h3 className="font-display text-3xl uppercase leading-tight text-nexa-bone transition-colors duration-300 group-hover:text-nexa-red md:text-4xl">
                    {c.name}
                  </h3>
                  <p className="mt-2 text-sm text-nexa-bone/60">{c.note}</p>
                </div>
              </Link>
            </FadeUp>
          ))}
        </div>

        <FadeUp className="mt-12">
          <Link
            to="/work"
            data-testid="selected-work-cta"
            className="group inline-flex items-center gap-3 border border-white/25 px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] text-nexa-bone transition-colors duration-200 hover:border-nexa-red hover:text-nexa-red"
          >
            Open the full ledger
            <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
        </FadeUp>
      </div>
    </section>
  );
};

const ServicesIndex = () => (
  <section className="border-t border-white/10" data-testid="services-index-section">
    <div className="mx-auto max-w-[1600px] px-5 py-24 md:px-10 md:py-36">
      <SectionTag>What we do</SectionTag>
      <h2 className="mt-4 font-display text-5xl uppercase leading-none text-nexa-bone md:text-7xl">
        Seven weapons.<br />
        <span className="text-nexa-red">One target.</span>
      </h2>
      <div className="mt-14 border-t border-white/10">
        {SERVICES.map((s, i) => (
          <FadeUp key={s.id} delay={i * 0.05}>
            <Link
              to={`/services#${s.id}`}
              data-testid={`service-row-${s.id}`}
              className="group grid items-baseline gap-2 border-b border-white/10 py-7 transition-colors duration-300 hover:bg-nexa-surface md:grid-cols-12 md:px-4"
            >
              <span className="font-mono text-sm text-nexa-red md:col-span-1">{s.num}</span>
              <span className="font-display text-3xl uppercase tracking-tight text-nexa-bone transition-colors duration-300 group-hover:text-nexa-red md:col-span-5 md:text-4xl">
                {s.title}
              </span>
              <span className="text-sm leading-relaxed text-nexa-bone/50 md:col-span-5">{s.line}</span>
              <ArrowRight size={18} className="hidden text-nexa-bone/30 transition-all duration-300 group-hover:translate-x-2 group-hover:text-nexa-red md:col-span-1 md:block" />
            </Link>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const SeoIntro = () => (
  <section className="bg-nexa-bone text-nexa-black" data-testid="seo-intro">
    <div className="mx-auto max-w-[1600px] px-5 py-20 md:px-10 md:py-28">
      <FadeUp>
        <h2 className="max-w-4xl font-display text-3xl uppercase leading-tight tracking-tight sm:text-4xl lg:text-5xl">
          Brand building and advertising agency in Kolkata.
        </h2>
        <div className="mt-8 max-w-3xl space-y-5 font-body text-base leading-relaxed text-nexa-black/70 md:text-lg">
          <p>
            Nexa Studio is a brand building agency in Kolkata with twelve years of work behind it. We are a branding company in Kolkata and a digital marketing agency in Kolkata, working as one advertising agency, so the brand and the ad spend never pull in different directions.
          </p>
          <p>
            Our social media management team in Kolkata runs daily content and community. Our performance marketing desk buys leads and sales, not impressions. Our content creation team produces in house, on our own shoot floor, which is also where our ad film makers work. We handle podcast production in Kolkata end to end, from format to release.
          </p>
          <p>
            Design is the seventh weapon. It covers logo design and brand identity, packaging, and websites. If you are looking for a website development company in Kolkata that builds the site as part of the brand and not as an afterthought, that is us too.
          </p>
        </div>
        <div className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-2 font-mono text-xs uppercase tracking-[0.2em]">
          <Link to="/services" className="link-line text-nexa-red" data-testid="seo-intro-services">
            See the services
          </Link>
          <span className="text-nexa-black/30">·</span>
          <Link to="/blog" className="link-line text-nexa-red" data-testid="seo-intro-blog">
            Read the journal
          </Link>
          <span className="text-nexa-black/30">·</span>
          <Link to="/contact" className="link-line text-nexa-red" data-testid="seo-intro-contact">
            Start a project
          </Link>
        </div>
      </FadeUp>
    </div>
  </section>
);

const ManifestoSnippet = () => {
  const ch = MANIFESTO[0];
  return (
    <section className="relative overflow-hidden bg-nexa-red" data-testid="manifesto-snippet">
      <div className="noise-overlay" />
      <div className="mx-auto max-w-[1600px] px-5 py-24 md:px-10 md:py-36">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-nexa-black/70">Manifesto — Chapter {ch.num}</p>
        <FadeUp>
          <h2 className="mt-6 max-w-5xl font-display text-5xl uppercase leading-[0.95] tracking-tight text-white sm:text-6xl lg:text-8xl">
            {ch.title}
          </h2>
        </FadeUp>
        <FadeUp delay={0.15}>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-white/80 md:text-lg">{ch.body}</p>
        </FadeUp>
        <FadeUp delay={0.25}>
          <Link to="/philosophy" className="link-line mt-8 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-nexa-black" data-testid="manifesto-cta">
            All six chapters <ArrowRight size={14} />
          </Link>
        </FadeUp>
      </div>
    </section>
  );
};

const Home = () => (
  <>
    <Hero />
    <RedRibbon items={SERVICES.map((s) => s.title)} />
    <Proposition />
    <SelectedWork />
    <LedgerMarquee names={WORK_PROJECTS.map((c) => c.name)} />
    <ServicesIndex />
    <SeoIntro />
    <ManifestoSnippet />
  </>
);

export default Home;
